/**
 * Role-scoped session management.
 *
 * Tokens are stored in httpOnly cookies (set by the backend) — JavaScript
 * cannot read them, and the browser sends them automatically. This module
 * manages only NON-SECRET session metadata: active role, user profile data,
 * and device identity.
 */

export const getUserKey = (role) => (role ? `user:${role}` : 'user');

/** Infer the active role from the current URL path. */
export const getActiveRole = () => {
  const path = window.location.pathname;
  if (path.startsWith('/astrologer')) return 'astrologer';
  if (path.startsWith('/admin')) return 'admin';
  return 'user';
};

/** Store session metadata for a specific role (tokens are in httpOnly cookies). */
export const storeSession = (role, user) => {
  localStorage.setItem(getUserKey(role), JSON.stringify(user));
  localStorage.setItem('user', JSON.stringify(user));
  localStorage.setItem('activeRole', role);
};

/** Get user data for a specific role. */
export const getUser = (role) => {
  const userStr =
    localStorage.getItem(getUserKey(role)) || localStorage.getItem('user');
  return userStr ? JSON.parse(userStr) : null;
};

/** Clear session metadata for a specific role. */
export const clearSession = (role) => {
  localStorage.removeItem(getUserKey(role));
};

/** Clear all session metadata (full logout). */
export const clearAllSessions = () => {
  ['user', 'astrologer', 'admin'].forEach((r) => {
    clearSession(r);
  });
  localStorage.removeItem('user');
  localStorage.removeItem('activeRole');
};

/** Get or create a persistent device ID for this browser. */
export const getDeviceId = () => {
  let id = localStorage.getItem('device_id');
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem('device_id', id);
  }
  return id;
};

/** Get a human-readable device name from the user agent. */
export const getDeviceName = () => {
  const ua = navigator.userAgent;
  if (ua.includes('Chrome') && !ua.includes('Edg')) return 'Chrome';
  if (ua.includes('Firefox')) return 'Firefox';
  if (ua.includes('Safari') && !ua.includes('Chrome')) return 'Safari';
  if (ua.includes('Edg')) return 'Edge';
  return ua.substring(0, 64);
};

// ---- Legacy compatibility shims (used by components not yet migrated) ----
// These no-op for tokens since tokens are now in httpOnly cookies.

/** @deprecated Tokens are now in httpOnly cookies. This returns null. */
export const getTokenKey = () => null;

/** @deprecated Tokens are now in httpOnly cookies. This is a no-op. */
export const storeRefreshToken = () => {};

/** @deprecated Tokens are now in httpOnly cookies. This returns null. */
export const getRefreshToken = () => null;

/** @deprecated Use getUser() instead. */
export const getSession = (role) => {
  return { token: null, user: getUser(role) };
};
